// « Client » view of the ticket window: who the parcel is for (recipient),
// the B2B client and how it was identified, the shop's customer record,
// everyone involved (« Intervenants », with the editable mail copy), the
// client's other tickets and the sender address. Sources: GET /context
// (recipient_info, client_info, sender_info), GET /api/shopify/get_customer,
// GET /api/clients/tickets/by-email. A block renders only when it has data.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { Badge } from '../design/ui';
import { getStatusLabel } from '../constants/ticketStatuses';
import { formatShortDateTime, ticketStatusTone } from './TicketWorkspaceHelpers';

const CLIENT_TICKETS_KEEP = 3;

const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';

const Section = ({ title, aside, children }) => (
    <section className="tkw-section">
        <div className="tkw-section__head">
            <h3 className="tkw-section__title">{title}</h3>
            {aside}
        </div>
        {children}
    </section>
);

const Facts = ({ items }) => {
    const shown = items.filter((item) => item && hasValue(item.value));
    if (!shown.length) return null;
    return (
        <dl className="tkw-dl">
            {shown.map((item) => (
                <div key={item.key}>
                    <dt>{item.label}</dt>
                    <dd>{item.node || item.value}</dd>
                </div>
            ))}
        </dl>
    );
};

const addressLine = (address) => {
    if (!address || typeof address !== 'object') return '';
    return [
        address.street1,
        address.street2,
        [address.postcode, address.city].filter(Boolean).join(' '),
        address.country,
    ].filter(Boolean).join(', ');
};

const TicketWorkspaceClient = ({
    loading, ticketId, customerEmail, recipient, sender, client, shopCustomer, shopCustomerLoading,
    clientTickets, clientTicketsLoading, clientTicketsError, onOpenTicket, actors,
}) => {
    const { t } = useTranslation();
    const [showAllTickets, setShowAllTickets] = useState(false);

    const recipientInfo = recipient || {};
    const clientInfo = client || {};
    const senderInfo = sender || {};
    const normalizedCustomer = String(customerEmail || '').trim().toLowerCase();
    const recipientEmail = clientInfo.email_client || recipientInfo.contact?.email || '';
    // The customer's address is already listed under « Intervenants ».
    const showRecipientEmail = recipientEmail && recipientEmail.trim().toLowerCase() !== normalizedCustomer;

    const clientSources = {
        sender_email: t('ticketWorkspaceUi.client.sourceSender', 'Reconnu à l’adresse de l’expéditeur'),
        ai_extraction: t('ticketWorkspaceUi.client.sourceAi', 'Identifié par l’IA dans la demande'),
        operator: t('ticketWorkspaceUi.client.sourceOperator', 'Choisi par une personne de l’équipe'),
    };

    const recipientFacts = [
        { key: 'name', label: t('messageWindow.name', 'Nom'), value: recipientInfo.name },
        { key: 'company', label: t('messageWindow.company', 'Société'), value: recipientInfo.company },
        { key: 'address', label: t('messageWindow.address', 'Adresse'), value: addressLine(recipientInfo.address) },
        { key: 'phone', label: t('messageWindow.phone', 'Téléphone'), value: recipientInfo.contact?.phone },
        showRecipientEmail && {
            key: 'email', label: t('messageWindow.email', 'E-mail'), value: recipientEmail,
            node: <a className="u-link" href={`mailto:${recipientEmail}`}>{recipientEmail}</a>,
        },
    ];
    const clientFacts = [
        { key: 'name', label: t('messageWindow.clientName', 'Client'), value: clientInfo.name },
        { key: 'id', label: t('messageWindow.clientId', 'Identifiant'), value: clientInfo.id, node: <code className="u-code">{clientInfo.id}</code> },
        { key: 'status', label: t('messageWindow.clientStatus', 'Statut'), value: clientInfo.status },
        { key: 'source', label: t('ticketWorkspaceUi.client.source', 'Identification'), value: clientSources[clientInfo.source] },
    ];
    const shopFacts = shopCustomer ? [
        { key: 'name', label: t('messageWindow.name', 'Nom'), value: [shopCustomer.first_name, shopCustomer.last_name].filter(Boolean).join(' ') },
        { key: 'email', label: t('messageWindow.email', 'E-mail'), value: shopCustomer.email },
        { key: 'phone', label: t('messageWindow.phone', 'Téléphone'), value: shopCustomer.phone },
    ] : [];
    const senderFacts = [
        { key: 'name', label: t('messageWindow.name', 'Nom'), value: senderInfo.name },
        { key: 'company', label: t('messageWindow.company', 'Société'), value: senderInfo.company },
        { key: 'address', label: t('messageWindow.address', 'Adresse'), value: addressLine(senderInfo.address) },
        { key: 'phone', label: t('messageWindow.phone', 'Téléphone'), value: senderInfo.contact?.phone },
        { key: 'email', label: t('messageWindow.email', 'E-mail'), value: senderInfo.contact?.email },
    ];
    const has = (facts) => facts.some((item) => item && hasValue(item.value));

    const tickets = Array.isArray(clientTickets) ? clientTickets : [];
    const shownTickets = showAllTickets ? tickets : tickets.slice(0, CLIENT_TICKETS_KEEP);

    return (
        <div className="tkw-side__view tkw-client">
            {loading ? (
                <div className="tkw-skeleton-stack" aria-busy="true">
                    <span className="u-skeleton tkw-skeleton-line tkw-skeleton-line--short" />
                    <span className="u-skeleton tkw-skeleton-block" />
                </div>
            ) : (
                <>
                    {has(recipientFacts) && (
                        <Section title={t('ticketWorkspaceUi.client.recipient', 'Destinataire')}>
                            <Facts items={recipientFacts} />
                        </Section>
                    )}
                    {has(clientFacts) && (
                        <Section title={t('ticketWorkspaceUi.client.b2b', 'Client B2B')}>
                            <Facts items={clientFacts} />
                        </Section>
                    )}
                </>
            )}

            {shopCustomerLoading && !shopCustomer && (
                <div className="tkw-skeleton-stack" aria-busy="true">
                    <span className="u-skeleton tkw-skeleton-line tkw-skeleton-line--short" />
                </div>
            )}
            {has(shopFacts) && (
                <Section title={t('ticketWorkspaceUi.client.shopCustomer', 'Fiche client de la boutique')}>
                    <Facts items={shopFacts} />
                </Section>
            )}

            <Section title={t('messageWindow.tabActors', 'Intervenants')}>
                {actors}
            </Section>

            {(clientTicketsLoading || clientTicketsError || tickets.length > 0) && (
                <Section
                    title={t('messageWindow.clientTickets', 'Tickets du client')}
                    aside={tickets.length > 0 && <span className="tkw-section__count">{tickets.length}</span>}
                >
                    {clientTicketsLoading && (
                        <div className="tkw-skeleton-stack" aria-busy="true">
                            <span className="u-skeleton tkw-skeleton-line" />
                            <span className="u-skeleton tkw-skeleton-line" />
                        </div>
                    )}
                    {!clientTicketsLoading && clientTicketsError && (
                        <p className="tkw-section__empty tkw-section__empty--error" role="alert">{clientTicketsError}</p>
                    )}
                    {!clientTicketsLoading && !clientTicketsError && tickets.length > 0 && (
                        <ul className="tkw-lines">
                            {shownTickets.map((clientTicket) => {
                                const isCurrent = clientTicket.id === ticketId;
                                const tone = ticketStatusTone(clientTicket.status);
                                return (
                                    <li key={clientTicket.id}>
                                        <button
                                            type="button"
                                            className={`tkw-lines__row tkw-lines__row--button${isCurrent ? ' is-current' : ''}`}
                                            onClick={() => { if (!isCurrent) onOpenTicket(clientTicket.id); }}
                                            disabled={isCurrent}
                                        >
                                            <span className="tkw-lines__main">
                                                <span className="tkw-lines__title">{clientTicket.subject || t('messageWindow.noSubject', 'Sans objet')}</span>
                                                <span className="tkw-lines__meta">
                                                    {isCurrent
                                                        ? t('messageWindow.currentTicket', 'Ticket actuel')
                                                        : formatShortDateTime(clientTicket.updated_at)}
                                                </span>
                                            </span>
                                            {clientTicket.status && (
                                                <Badge tone={tone}>{getStatusLabel(t, clientTicket.status)}</Badge>
                                            )}
                                            {!isCurrent && <ChevronRight className="tkw-lines__chevron" strokeWidth={2} aria-hidden="true" />}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {tickets.length > CLIENT_TICKETS_KEEP && (
                        <button
                            type="button"
                            className="u-btn u-btn--ghost u-btn--sm tkw-section__more"
                            onClick={() => setShowAllTickets((value) => !value)}
                            aria-expanded={showAllTickets}
                        >
                            {showAllTickets
                                ? t('messageWindow.showLess', 'Voir moins')
                                : t('ticketWorkspaceUi.client.moreTickets', 'Voir les {{count}} autres', { count: tickets.length - CLIENT_TICKETS_KEEP })}
                        </button>
                    )}
                </Section>
            )}

            {!loading && has(senderFacts) && (
                <Section title={t('ticketWorkspaceUi.client.sender', 'Expéditeur')}>
                    <Facts items={senderFacts} />
                </Section>
            )}
        </div>
    );
};

export default TicketWorkspaceClient;
